'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useDynamicContext, useIsLoggedIn } from '@dynamic-labs/sdk-react-core';
import { useAccount } from 'wagmi';
import { useChainConfig } from './use-chain-config';

export type WalletSessionStatus =
  | 'loading'
  | 'disconnected'
  | 'connecting'
  | 'restoring'
  | 'mismatch'
  | 'network-unavailable'
  | 'wrong-network'
  | 'ready';

interface UseWalletSessionResult {
  status: WalletSessionStatus;
  sdkHasLoaded: boolean;
  /** Dynamic's authenticated session state, independent of provider state. */
  isAuthenticated: boolean;
  /** Dynamic primary wallet address: the identity used for private data. */
  identityAddress: string | undefined;
  /** Compatibility alias for consumers that use Dynamic's terminology. */
  primaryAddress: string | undefined;
  /** Active address reported by wagmi/the browser wallet provider. */
  providerAddress: `0x${string}` | undefined;
  /**
   * True when the authenticated identity and selected network are safe for
   * private read requests. Browser-wallet signing health is intentionally
   * separate so locking or disconnecting an extension never hides reports.
   */
  isIdentityReady: boolean;
  /** Compatibility alias for identity/read readiness. */
  isReady: boolean;
  isSupportedChain: boolean;
  /** Starts authentication only from a settled disconnected state. */
  connect: () => void;
  /** Ends the authenticated Dynamic session and its wallet connections. */
  disconnect: () => Promise<void>;
  openWalletManager: () => void;
  /** Retries ROWA's operator-managed network availability request. */
  retryNetworkAvailability: () => void;
}

function addressesMatch(
  identityAddress: string | undefined,
  providerAddress: string | undefined
) {
  return (
    identityAddress != null &&
    providerAddress != null &&
    identityAddress.toLowerCase() === providerAddress.toLowerCase()
  );
}

/**
 * The shared wallet-session state machine.
 *
 * Dynamic owns authenticated identity; wagmi reflects the browser provider.
 * Private reads require the authenticated identity plus an enabled network.
 * Signing readiness is deliberately checked by WalletSignerHealthProvider,
 * because Dynamic's wagmi adapter can retain an address after the extension
 * has lost permission. Never use `isReady` alone to authorize a write.
 */
export function useWalletSession(): UseWalletSessionResult {
  const {
    sdkHasLoaded,
    primaryWallet,
    accountSwitchState,
    awaitingSignatureState,
    multiWalletWidgetState,
    showAuthFlow,
    setShowAuthFlow,
    setShowDynamicUserProfile,
    handleLogOut
  } = useDynamicContext();
  const isLoggedIn = useIsLoggedIn();
  const disconnectInFlightRef = useRef<Promise<void> | null>(null);
  const { address: providerAddress, status: providerStatus } = useAccount();
  const {
    isSupported: isSupportedChain,
    isEffectiveChainSupported,
    isAvailabilityPending,
    isAvailabilityError,
    retryAvailability: retryNetworkAvailability
  } = useChainConfig();

  const identityAddress = primaryWallet?.address;
  const isAuthenticated = sdkHasLoaded && isLoggedIn;

  // Dynamic 4.96 removed the old `isVerificationInProgress` boolean. These
  // are its current, typed equivalents for an in-flight wallet connection or
  // signature verification.
  const isVerificationInProgress =
    awaitingSignatureState != null && awaitingSignatureState !== 'idle';
  const isWalletOperationInProgress =
    multiWalletWidgetState != null && multiWalletWidgetState !== 'idle';
  const isPrimaryWalletSwitchInProgress =
    accountSwitchState === 'linking_new_wallet' ||
    accountSwitchState === 'switching_primary';

  const status = useMemo<WalletSessionStatus>(() => {
    if (!sdkHasLoaded) return 'loading';

    if (providerStatus === 'reconnecting') return 'restoring';

    // This is Dynamic's explicit signal for the MetaMask-style state where
    // the authenticated account and the wallet extension's active account
    // diverge. It takes precedence over the still-open auth modal.
    if (isLoggedIn && accountSwitchState === 'primary_not_connected') {
      return 'mismatch';
    }

    if (
      providerStatus === 'connecting' ||
      showAuthFlow ||
      isVerificationInProgress ||
      isWalletOperationInProgress ||
      isPrimaryWalletSwitchInProgress
    ) {
      return 'connecting';
    }

    if (!isLoggedIn) return 'disconnected';

    // Dynamic can restore its user before it has hydrated the primary wallet.
    if (!identityAddress) return 'restoring';

    if (!addressesMatch(identityAddress, providerAddress)) {
      return 'mismatch';
    }

    if (isAvailabilityPending) return 'restoring';

    if (isAvailabilityError) return 'network-unavailable';

    if (!isSupportedChain) return 'wrong-network';

    return 'ready';
  }, [
    accountSwitchState,
    identityAddress,
    isLoggedIn,
    isPrimaryWalletSwitchInProgress,
    isAvailabilityError,
    isAvailabilityPending,
    isSupportedChain,
    isVerificationInProgress,
    isWalletOperationInProgress,
    providerAddress,
    providerStatus,
    sdkHasLoaded,
    showAuthFlow
  ]);

  const isIdentityReady = Boolean(
    isAuthenticated &&
      identityAddress &&
      !isPrimaryWalletSwitchInProgress &&
      !isAvailabilityPending &&
      !isAvailabilityError &&
      isEffectiveChainSupported
  );

  const connect = useCallback(() => {
    // Prevent a second authentication/signature request while Dynamic is
    // already handling one, and never restart auth for a settled session.
    if (status !== 'disconnected') return;
    setShowAuthFlow(true);
  }, [setShowAuthFlow, status]);

  const disconnect = useCallback(() => {
    if (disconnectInFlightRef.current) {
      return disconnectInFlightRef.current;
    }

    // Start from a settled promise so even an unexpected synchronous SDK
    // failure follows the same address-free error path. Dynamic owns both the
    // authenticated session and connector cleanup; wagmi-only disconnects can
    // leave those states divergent.
    const operation = Promise.resolve()
      .then(() => handleLogOut())
      .catch(() => {
        const error = new Error('Wallet disconnect failed');
        error.name = 'WalletDisconnectError';
        throw error;
      })
      .finally(() => {
        if (disconnectInFlightRef.current === operation) {
          disconnectInFlightRef.current = null;
        }
      });

    disconnectInFlightRef.current = operation;
    return operation;
  }, [handleLogOut]);

  const openWalletManager = useCallback(() => {
    if (isAuthenticated) {
      setShowDynamicUserProfile(true);
      return;
    }

    connect();
  }, [connect, isAuthenticated, setShowDynamicUserProfile]);

  return {
    status,
    sdkHasLoaded,
    isAuthenticated,
    identityAddress,
    primaryAddress: identityAddress,
    providerAddress,
    isIdentityReady,
    isReady: isIdentityReady,
    isSupportedChain,
    connect,
    disconnect,
    openWalletManager,
    retryNetworkAvailability
  };
}
